/**
 * Явные измерения контекста подбора: активность, транспорт, длительность и разовый сдвиг
 * предпочтения (CTHS-153/154/161).
 *
 * Подписи живут рядом с кодами, а не в разметке: их видят и чипы расширенной панели, и
 * резюме «Будет учтено». Разойтись они не могут по построению — иначе пользователь выбирал
 * бы одно, а в резюме читал другое.
 */

export type OutfitActivity =
    'MINIMAL' | 'MODERATE' | 'LONG_WALK' | 'SPORT' | 'OUTDOOR' | 'HANDS_ON' | 'TRAVEL';
export type OutfitTransport = 'ON_FOOT' | 'CAR' | 'TAXI' | 'PUBLIC' | 'BIKE' | 'SCOOTER';
export type OutfitDuration = 'UNDER_2H' | 'HALF_DAY' | 'ALL_DAY' | 'MULTI_DAY';
export type OutfitPreference = 'WARMER' | 'LIGHTER' | 'DRESSIER' | 'SIMPLER';

/** Выбранные измерения; `null` в поле — измерение не задано. */
export interface OutfitContextParams {
    activity: OutfitActivity | null;
    transport: OutfitTransport | null;
    duration: OutfitDuration | null;
    preference: OutfitPreference | null;
}

export const EMPTY_CONTEXT: OutfitContextParams = {
    activity: null, transport: null, duration: null, preference: null,
};

export const ACTIVITY_LABELS: Record<OutfitActivity, string> = {
    MINIMAL: 'Сидячая',
    MODERATE: 'Умеренная ходьба',
    LONG_WALK: 'Долгая ходьба',
    SPORT: 'Спорт',
    OUTDOOR: 'На улице',
    HANDS_ON: 'Работа руками',
    TRAVEL: 'В дороге',
};

export const TRANSPORT_LABELS: Record<OutfitTransport, string> = {
    ON_FOOT: 'Пешком',
    CAR: 'Автомобиль',
    TAXI: 'Такси',
    PUBLIC: 'Общественный',
    BIKE: 'Велосипед',
    SCOOTER: 'Самокат',
};

export const DURATION_LABELS: Record<OutfitDuration, string> = {
    UNDER_2H: 'До двух часов',
    HALF_DAY: 'Полдня',
    ALL_DAY: 'Весь день',
    MULTI_DAY: 'Несколько дней',
};

export const PREFERENCE_LABELS: Record<OutfitPreference, string> = {
    WARMER: 'Теплее',
    LIGHTER: 'Легче',
    DRESSIER: 'Наряднее',
    SIMPLER: 'Проще',
};

/** Сценарный тайл быстрого подбора (`GET /api/outfits/scenarios`). */
export interface OutfitScenarioTile {
    key: string;
    nameRu: string;
    iconCode: string;
    occasion: string;
    activity?: OutfitActivity | null;
    transport?: OutfitTransport | null;
    duration?: OutfitDuration | null;
    preference?: OutfitPreference | null;
    hints?: string[] | null;
}

/** Задано ли хоть одно измерение — от этого зависит, разворачивать ли панель по умолчанию. */
export function hasContextParams(params: OutfitContextParams): boolean {
    return Boolean(params.activity || params.transport || params.duration || params.preference);
}

/**
 * Строки резюме «Будет учтено» по выбранным измерениям. Сюда попадает только выбранное:
 * интерфейс не обещает учесть то, чего пользователь не выбирал.
 */
export function contextParamsSummary(params: OutfitContextParams): string[] {
    const out: string[] = [];
    if (params.activity) out.push(`Активность: ${ACTIVITY_LABELS[params.activity]}`);
    if (params.transport) out.push(`Транспорт: ${TRANSPORT_LABELS[params.transport]}`);
    if (params.duration) out.push(`Длительность: ${DURATION_LABELS[params.duration]}`);
    if (params.preference) out.push(`Предпочтение: ${PREFERENCE_LABELS[params.preference]}`);
    return out;
}

/** Тело запроса: незаданные измерения не отправляются вовсе, а не уходят как `null`. */
export function contextRequestBody(params: OutfitContextParams): Record<string, string> {
    const body: Record<string, string> = {};
    if (params.activity) body.activity = params.activity;
    if (params.transport) body.transport = params.transport;
    if (params.duration) body.duration = params.duration;
    if (params.preference) body.preference = params.preference;
    return body;
}

/** Измерения тайла как параметры запроса — расширенная панель показывает их уже выбранными. */
export function paramsOfTile(tile: OutfitScenarioTile): OutfitContextParams {
    return {
        activity: tile.activity ?? null,
        transport: tile.transport ?? null,
        duration: tile.duration ?? null,
        preference: tile.preference ?? null,
    };
}
